import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericPromptDialog from "./GenericPromptDialog/GenericPromptDialog";

/**
 * What a declined confirmation rejects with: the save didn't happen, the same
 * way a failed validation rejects with "VALIDATION_ERROR".
 */
export const DELETED_EXTRA_CO2_TYPES_DECLINED =
  "DELETED_EXTRA_CO2_TYPES_DECLINED";

/**
 * A save (or a copy) refused with ERR_DELETED_EXTRA_CO2_TYPES asks the user
 * whether to go on without the extra CO2 lines whose type was deleted.
 * `ask()` shows the question and resolves with the answer; render `dialog`
 * in the component.
 */
export const useConfirmDropDeletedExtraCo2Types = (
  kind: "SAVE" | "COPY"
): { ask: () => Promise<boolean>; dialog: ReactNode } => {
  const { t } = useTranslation("extraCO2Types");
  const [answer, setAnswer] = useState<((confirmed: boolean) => void) | null>(
    null
  );

  const ask = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        setAnswer(() => resolve);
      }),
    []
  );

  const reply = (confirmed: boolean) => {
    answer?.(confirmed);
    setAnswer(null);
  };

  const dialog = answer && (
    <GenericPromptDialog
      title={t("dropDeletedLinesDialog.title")}
      text={
        kind === "COPY"
          ? t("dropDeletedLinesDialog.copyText")
          : t("dropDeletedLinesDialog.saveText")
      }
      continueText={t("dropDeletedLinesDialog.continue")}
      onCancelClick={() => reply(false)}
      onContinueClick={() => reply(true)}
      continueButtonColor="primary"
    />
  );

  return { ask, dialog };
};
